import { useEffect } from 'react'
import { BrandSettingsContent } from '@/components/BrandSettingsContent'

interface Props {
  brandId: string
  brandName: string
  onClose: () => void
  onDeleted: () => void
}

export function BrandSettingsModal({ brandId, brandName, onClose, onDeleted }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-md flex md:items-center md:justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Mobile: tap area above the sheet to close */}
      <div className="md:hidden fixed inset-x-0 top-0 h-16" onClick={onClose} />

      {/* Desktop: centered panel */}
      <div className="hidden md:flex w-full max-w-2xl mx-4 flex-col bg-surface-container border border-outline-variant/20 rounded-2xl shadow-2xl max-h-[90vh] animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{brandName}</span>
            <span className="text-outline-variant/40 text-sm">/</span>
            <span className="text-outline text-sm">Settings</span>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-outline hover:text-white hover:bg-white/5 transition-colors text-xl leading-none"
            title="Close"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-6">
          <BrandSettingsContent brandId={brandId} onDeleted={onDeleted} />
        </div>
      </div>

      {/* Mobile: full-screen slide-up panel */}
      <div className="flex md:hidden fixed inset-x-0 bottom-0 top-16 flex-col bg-surface-container rounded-t-2xl animate-slide-up">
        <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
          <div className="w-12 h-1.5 rounded-full bg-outline-variant/40" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-outline-variant/10 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{brandName}</span>
            <span className="text-outline-variant/40 text-sm">/</span>
            <span className="text-outline text-sm">Settings</span>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-outline hover:text-white transition-colors text-xl leading-none"
            title="Close"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-5">
          <BrandSettingsContent brandId={brandId} onDeleted={onDeleted} />
        </div>
      </div>
    </div>
  )
}
